import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DialogService } from 'src/app/core/dialog.service';
import { Store } from 'src/app/core/stores.service';
import { UtilsService } from 'src/app/core/utils.service';
import { STORES_TABLE_COLUMNS } from 'src/app/shared/constants/operationals';
import { StoresToggleDialogComponent } from '../stores-toggle-dialog/stores-toggle-dialog.component';
import { StoresTicketDialogComponent } from '../stores-ticket-dialog/stores-ticket-dialog.component';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { StoresQrCodeDialogComponent } from '../stores-qr-code-dialog/stores-qr-code-dialog.component';
import { StoresPluginCredentialsDialogComponent } from '../stores-plugin-credentials-dialog/stores-plugin-credentials-dialog.component';
import { StoresPaymentMethodsDialogComponent } from '../stores-payment-methods-dialog/stores-payment-methods-dialog.component';
import { StoresAllowedStepsDialogComponent } from '../stores-allowed-steps-dialog/stores-allowed-steps-dialog.component';
import { NavigationService } from 'src/app/core/navigation.service';
import { StoresSalesNetworkDialogComponent } from '../stores-sales-network-dialog/stores-sales-network-dialog.component';

@Component({
  selector: 'app-stores-data-table',
  templateUrl: './stores-data-table.component.html',
  styleUrls: ['./stores-data-table.component.scss'],
})
export class StoresDataTableComponent {
  @Input() stores: Store[] = [];
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() scrolledToEnd: EventEmitter<void> = new EventEmitter<void>();

  displayed_columns: string[] = STORES_TABLE_COLUMNS;

  constructor(
    private utils_service: UtilsService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private navigation_service: NavigationService,
  ) {}

  onAllowedStepsClick(store: Store): void {
    this.dialog_service.openDialog(StoresAllowedStepsDialogComponent, { store });
  }

  onTicketClick(store: Store): void {
    this.dialog_service
      .openDialog(StoresTicketDialogComponent, { store })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const end_offset = 200;
    if (element) {
      if (element.offsetHeight + element.scrollTop >= element.scrollHeight - end_offset) {
        this.scrolledToEnd.emit();
      }
    }
  }

  onEnableDisableClick(store: Store): void {
    this.dialog_service
      .openDialog(StoresToggleDialogComponent, { store })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onQrCodeClick(store: Store): void {
    this.dialog_service.openDialog(StoresQrCodeDialogComponent, {
      store,
    });
  }

  onPluginCredentialsClick(store: Store): void {
    this.dialog_service
      .openDialog(StoresPluginCredentialsDialogComponent, {
        store,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onStoreQineticPsClick(store: Store): void {
    this.dialog_service.openDialog(StoresPaymentMethodsDialogComponent, {
      store_id: store.store_id,
      company_id: store.company_id,
    });
  }

  onDetailsClick(store: Store): void {
    this.navigation_service.toStoreDetails(store.company_id as string, store.store_id as string);
  }

  getStoreStatusBackgroundClass(store?: Store): { [key: string]: boolean | undefined } {
    if (!store) return {};
    return this.utils_service.getStoreStatusBackgroundClass(store);
  }

  getStoreStatusLabel(store?: Store): string {
    if (!store) return '';
    return this.utils_service.getStoreStatusLabel(store);
  }

  getStoreTypeLabel(store?: Store): string {
    const is_web_sale = store?.order_requirements?.is_web_sale_enabled;
    const is_in_store_sale = store?.order_requirements?.is_in_store_sale_enabled;

    if (is_web_sale && is_in_store_sale) {
      return 'STORES.PHYSICAL_STORES_AND_ECOMMERCE';
    }
    if (is_web_sale) {
      return 'STORES.ECOMMERCE_ONLY';
    }
    if (is_in_store_sale) {
      return 'STORES.PHYSICAL_STORE_ONLY';
    }

    return '-';
  }

  onSetSalesNetworkIdClick(store: Store): void {
    console.log('onSetSalesNetworkIdClick', store);
    this.dialog_service
      .openDialog(StoresSalesNetworkDialogComponent, {
        store_id: store.store_id,
        store_sales_network_id: store.sales_network_id,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  isSalesNetworkIDEditable(store: Store): boolean {
    return !store.is_sales_network_id_editing_disabled;
  }
}
